import AddIcon from "@mui/icons-material/Add";
import AddTaskIcon from "@mui/icons-material/AddTask";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import DeleteIcon from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../../components/ConfirmDialog";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";
import AdminCrossBoardService from "../AdminCrossBoardService";
import Logger from "../../../../services/Logger";
import WordsNotInUseFrenchNotice from "../../../pzl_common/WordsNotInUseFrenchNotice";
import CrossCandEntry from "./CrossCandEntry";
import { buildSquareFromFilledCoordinates, findEmptySpaces, generatePositions } from "./CrossCandUtils";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

export default function CrossCanAssoc({ hwTpl, square, onWordAdd, onShowSnackbar, onWordSelect, onRefresh }) {
    const isFrenchPuzzle = String(hwTpl?.language ?? "").toLowerCase() === "french";
    const [puzzleWords, setPuzzleWords] = useState([]);
    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedAssociatedWord, setSelectedAssociatedWord] = useState(null);
    const [associatedWords, setAssociatedWords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [wordToPlace, setWordToPlace] = useState(null);
    const [selectedWordForPlacement, setSelectedWordForPlacement] = useState(null);
    const [emptySpaces, setEmptySpaces] = useState([]);
    const [candidateWords, setCandidateWords] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [deleteWordDialogOpen, setDeleteWordDialogOpen] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);
    const [filterByLength, setFilterByLength] = useState(false);
    const [wordsNotInUse, setWordsNotInUse] = useState([]);
    const [filterWordsNotInUseByLength, setFilterWordsNotInUseByLength] = useState(false);
    const [addAllWordsDialogOpen, setAddAllWordsDialogOpen] = useState(false);

    // Check for empty spaces when square changes
    useEffect(() => {
        if (square && square.length > 0) {
            const spaces = findEmptySpaces(square);
            setEmptySpaces(spaces);
        } else {
            setEmptySpaces([]);
        }
    }, [square]);

    // Extract words from filledCoordinates
    useEffect(() => {
        if (!hwTpl?.filledCoordinates) {
            setPuzzleWords([]);
            return;
        }
        try {
            const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
            const words = filledCoordinates
                .filter(word => word.text) // Filter out words without text
                .map((word) => {
                    return word.text;
                });
            // Sort words alphabetically
            const sortedWords = [...words].sort((a, b) => a.localeCompare(b));
            setPuzzleWords(sortedWords);
        } catch (error) {
            Logger.error("Error parsing filledCoordinates:", error);
            setPuzzleWords([]);
        }
    }, [hwTpl?.filledCoordinates]);

    // Extract words from wordsNotInUse (from word network - words not yet placed in puzzle)
    // Fallback: derive from wordNetwork nodes when wordsNotInUse is not provided by API
    useEffect(() => {
        const wordsFromApi = () => {
            if (!hwTpl?.wordsNotInUse) return [];
            try {
                return (typeof hwTpl.wordsNotInUse === 'string' ? hwTpl.wordsNotInUse : String(hwTpl.wordsNotInUse || ''))
                    .split('\n')
                    .map(word => word.trim())
                    .filter(word => word.length > 0);
            } catch (error) {
                Logger.error("Error parsing wordsNotInUse:", error);
                return [];
            }
        };

        const apiWords = wordsFromApi();
        if (apiWords.length > 0) {
            setWordsNotInUse([...apiWords].sort((a, b) => a.localeCompare(b)));
            return;
        }

        // Fallback: derive from word network nodes (words in network not yet in puzzle)
        const wordNetwork = hwTpl?.template?.wordNetwork || hwTpl?.wordNetwork;
        if (!wordNetwork?.nodes || !Array.isArray(wordNetwork.nodes)) {
            setWordsNotInUse([]);
            return;
        }

        // Get used words from filledCoordinates directly (avoid stale puzzleWords)
        const usedWords = new Set();
        if (hwTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                filledCoordinates.forEach(w => {
                    if (w?.text) usedWords.add(String(w.text).toUpperCase());
                });
            } catch (e) { /* ignore */ }
        }
        const notInUse = wordNetwork.nodes
            .map(node => (node.label || node.id || '').trim())
            .filter(word => word.length > 0 && !usedWords.has(word.toUpperCase()));
        const unique = [...new Set(notInUse)];
        setWordsNotInUse(unique.sort((a, b) => a.localeCompare(b)));
    }, [hwTpl?.wordsNotInUse, hwTpl?.template?.wordNetwork, hwTpl?.wordNetwork, puzzleWords]);

    // Refresh candidate words when words are placed (when filledCoordinates change)
    useEffect(() => {
        if (selectedWord) {
            findCandidateWordsForWord(selectedWord);
        }
    }, [hwTpl?.filledCoordinates, selectedWord]);

    // Helper function to find candidate words for a specific word
    const findCandidateWordsForWord = async (puzzleWord) => {
        if (!puzzleWord) {
            setCandidateWords([]);
            return;
        }

        // Calculate filled cells inline to avoid dependency issues
        let filledCellsCount = 0;
        if (hwTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                const uniqueCells = new Set();
                filledCoordinates.forEach(word => {
                    if (word.text && word.positions) {
                        word.positions.forEach(pos => {
                            uniqueCells.add(`${pos.row}-${pos.col}`);
                        });
                    }
                });
                filledCellsCount = uniqueCells.size;
            } catch (error) {
                Logger.error("Error calculating filled cells:", error);
            }
        }

        const emptyCellCount = 144 - filledCellsCount;
        if (emptyCellCount <= 0) {
            setCandidateWords([]);
            return;
        }

        setLoadingCandidates(true);
        try {
            // Get used words set
            const usedWords = new Set(puzzleWords.map(w => w.toUpperCase()));

            // Collect candidate words from synonyms and associations for the selected word
            const allCandidates = new Map(); // Use Map to avoid duplicates

            try {
                const language = hwTpl?.language || 'english';
                
                // Get word associations (synonyms and related words)
                const result = await DictionaryApiService.getWordAssociations(puzzleWord, language);
                const synonyms = result.synonyms || [];
                const associations = result.relatedWords || [];
                
                Logger.log(`Found ${synonyms.length} synonyms and ${associations.length} associations for "${puzzleWord}"`);
                synonyms.forEach(item => {
                    const word = item.word.toUpperCase();
                    if (!usedWords.has(word) && word.length <= emptyCellCount) {
                        if (!allCandidates.has(word)) {
                            allCandidates.set(word, {
                                word: word,
                                source: puzzleWord,
                                type: 'synonym',
                                score: item.score || 0
                            });
                        }
                    }
                });

                associations.forEach(item => {
                    const word = item.word.toUpperCase();
                    if (!usedWords.has(word) && word.length <= emptyCellCount) {
                        if (!allCandidates.has(word)) {
                            allCandidates.set(word, {
                                word: word,
                                source: puzzleWord,
                                type: 'association',
                                score: item.score || 0
                            });
                        }
                    }
                });
            } catch (error) {
                Logger.error(`Error finding candidates for "${puzzleWord}":`, error);
            }

            Logger.log(`Total candidates collected: ${allCandidates.size}`);
            Logger.log(`Empty cell count: ${emptyCellCount}`);

            // Convert to array, sort by word length (descending), and limit to 10
            const candidatesArray = Array.from(allCandidates.values())
                .sort((a, b) => b.word.length - a.word.length)
                .slice(0, 10);

            setCandidateWords(candidatesArray);
        } catch (error) {
            Logger.error("Error finding candidate words:", error);
            setCandidateWords([]);
        } finally {
            setLoadingCandidates(false);
        }
    };

    // Handle word selection and search for associations
    const handleWordSelect = async (word) => {
        setSelectedWord(word);
        setSelectedAssociatedWord(null); // Clear associated word selection
        setLoading(true);
        setAssociatedWords([]);
        setCandidateWords([]); // Clear previous candidates

        // Find the word data from filledCoordinates for highlighting
        let wordDataForHighlighting = null;
        if (hwTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                
                // Find word by matching text
                wordDataForHighlighting = filledCoordinates.find(w => {
                    if (!w.text) return false;
                    return w.text.toUpperCase() === word.toUpperCase();
                });
            } catch (error) {
                Logger.error("Error parsing filled coordinates:", error);
            }
        }

        // Notify parent component about puzzle word selection
        if (onWordSelect) {
            onWordSelect(wordDataForHighlighting || { text: word });
        }

        // Find candidate words for the selected word
        await findCandidateWordsForWord(word);

        try {
            const language = hwTpl?.language || 'english';
            
            // Search for associated words using Dictionary API
            const result = await DictionaryApiService.getWordAssociations(word.toLowerCase(), language);
            const results = result.relatedWords || [];

            if (results && results.length > 0) {
                Logger.log(`Found ${results.length} associations for "${word}"`);
                
                // Filter out words that are already used in the puzzle
                let usedWords = new Set();
                if (hwTpl?.filledCoordinates) {
                    try {
                        const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                        const words = filledCoordinates
                            .filter(w => w.text) // Filter out words without text
                            .map(w => {
                                // Remove hyphens for comparison
                                const text = w.text || '';
                                return text.replace(/-/g, '').toUpperCase();
                            });
                        usedWords = new Set(words);
                    } catch (error) {
                        Logger.error("Error parsing filledCoordinates:", error);
                    }
                }
                
                // Filter results - compare without hyphens
                const filteredResults = results.filter(result => {
                    if (!result || !result.word) return false;
                    const resultWordWithoutHyphens = result.word.replace(/-/g, '').toUpperCase();
                    return !usedWords.has(resultWordWithoutHyphens);
                });

                Logger.log(`After filtering used words: ${filteredResults.length} words remaining`);

                // Check if each word can fit in the puzzle
                const wordsWithPositions = await checkWordFitInPuzzle(filteredResults);
                Logger.log(`Words with positions: ${wordsWithPositions.length}`);
                setAssociatedWords(wordsWithPositions);
            } else {
                setAssociatedWords([]);
            }
        } catch (error) {
            Logger.error("Error searching for associated words:", error);
            onShowSnackbar("Error searching for associated words", "error");
            setAssociatedWords([]);
        } finally {
            setLoading(false);
        }
    };

    // Normalize a single letter for comparison (French mode)
    const normalizeLetter = (letter, language) => {
        if (!letter || typeof letter !== 'string') return letter;
        if (language === 'french') {
            return normalizeFrenchWord(letter);
        }
        return letter.toLowerCase();
    };

    // Check if a word can fit in the puzzle and find its position
    const checkWordFitInPuzzle = async (words) => {
        if (!square || square.length === 0) return [];

        const emptySpaces = findEmptySpaces(square);
        const wordsWithPositions = [];
        const language = hwTpl?.language || 'english';

        for (const wordData of words) {
            const word = wordData.word.toUpperCase();

            // Find all possible positions for this word
            for (const space of emptySpaces) {
                if (space.pattern.length === word.length) {
                    // Check if word fits the pattern
                    let fits = true;
                    for (let i = 0; i < space.pattern.length; i++) {
                        const patternChar = space.pattern[i];
                        const wordChar = word[i];

                        // If pattern has a letter, word must match it (normalize for French)
                        if (patternChar !== '?') {
                            const normalizedPattern = normalizeLetter(patternChar, language);
                            const normalizedWord = normalizeLetter(wordChar, language);
                            if (normalizedPattern !== normalizedWord) {
                                fits = false;
                                break;
                            }
                        }
                    }

                    if (fits) {
                        // Generate positions for this word
                        const positions = generatePositions(
                            space.startRow,
                            space.startCol,
                            space.direction,
                            word.length,
                            word
                        );

                        wordsWithPositions.push({
                            ...wordData,
                            word: word,
                            positions: positions,
                            startRow: space.startRow,
                            startCol: space.startCol,
                            direction: space.direction,
                            pattern: space.pattern,
                            consecutivenessScore: space.consecutivenessScore
                        });
                        break; // Found a position, move to next word
                    }
                }
            }
        }

        return wordsWithPositions;
    };

    // Handle adding an associated word to the puzzle
    const handleAddAssociatedWord = (wordData) => {
        if (onWordAdd && wordData) {
            onWordAdd(wordData);
            // Clear the associated words list after adding
            setAssociatedWords([]);
            setSelectedAssociatedWord(null);
            // Refresh candidate words after word is placed
            if (selectedWord) {
                findCandidateWordsForWord(selectedWord);
            }
        }
    };

    // Handle associated word selection
    const handleAssociatedWordSelect = (wordData) => {
        setSelectedAssociatedWord(wordData);

        // Notify parent component about associated word selection for highlighting
        if (onWordSelect) {
            onWordSelect(wordData);
        }
    };

    // Pagination constants
    const WORDS_PER_PAGE = 10;
    const totalPages = Math.ceil(associatedWords.length / WORDS_PER_PAGE);
    const startIndex = (currentPage - 1) * WORDS_PER_PAGE;
    const endIndex = startIndex + WORDS_PER_PAGE;
    const paginatedWords = useMemo(() => {
        return associatedWords.slice(startIndex, endIndex);
    }, [associatedWords, startIndex, endIndex]);

    // Reset to page 1 when associatedWords change
    useEffect(() => {
        setCurrentPage(1);
    }, [associatedWords.length]);

    // Pagination handlers
    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    // Handle word not in puzzle selection - select the word for placement (keep puzzle word selected)
    const handleWordNotInPuzzleSelect = async (word) => {
        setSelectedWordForPlacement(word);
        // Don't change selectedWord - keep the puzzle word selected
        setSelectedAssociatedWord(null); // Clear associated word selection
        setLoading(true);
        setAssociatedWords([]);

        // Also notify parent component about word selection for definition lookup
        if (onWordSelect) {
            // Create a word object for consistency
            const wordObject = { text: word };
            onWordSelect(wordObject);
        }

        try {
            const language = hwTpl?.language || 'english';
            
            // Search for associated words using Dictionary API
            const result = await DictionaryApiService.getWordAssociations(word.toLowerCase(), language);
            const results = result.relatedWords || [];

            if (results && results.length > 0) {
                // Filter out words that are already used in the puzzle
                let usedWords = new Set();
                if (hwTpl?.filledCoordinates) {
                    try {
                        const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                        const words = filledCoordinates
                            .filter(w => w.text) // Filter out words without text
                            .map(w => w.text.toUpperCase());
                        usedWords = new Set(words);
                    } catch (error) {
                        Logger.error("Error parsing filledCoordinates:", error);
                    }
                }
                const filteredResults = results.filter(result =>
                    !usedWords.has(result.word.toUpperCase())
                );

                // Check if each word can fit in the puzzle
                const wordsWithPositions = await checkWordFitInPuzzle(filteredResults);
                setAssociatedWords(wordsWithPositions);
            } else {
                setAssociatedWords([]);
            }
        } catch (error) {
            Logger.error("Error searching for associated words:", error);
            onShowSnackbar("Error searching for associated words", "error");
            setAssociatedWords([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle open confirmation dialog
    const handleOpenPlacementDialog = () => {
        if (selectedWordForPlacement) {
            setWordToPlace(selectedWordForPlacement);
            setConfirmDialogOpen(true);
        }
    };

    // Handle confirmation and place word in puzzle
    const handleConfirmWordPlacement = async () => {
        if (!wordToPlace) return;

        try {
            // Normalize French words by removing accents
            const language = hwTpl?.language || 'english';
            let normalizedWord = wordToPlace;
            if (language === 'french') {
                normalizedWord = normalizeFrenchWord(wordToPlace);
            } else {
                normalizedWord = wordToPlace.toLowerCase();
            }

            const wordLength = normalizedWord.length;
            const positions = [];

            // Get current filled positions to avoid conflicts
            const currentFilledPositions = new Set();

            // Check the actual puzzle grid for filled cells
            if (square) {
                Logger.log("Checking square array for filled cells:");
                for (let row = 0; row < 12; row++) {
                    for (let col = 0; col < 12; col++) {
                        const char = square[row][col];
                        Logger.log(`Cell (${row},${col}): "${char}" - isEmpty: ${!char || char === ""}`);
                        if (char && char !== "") {
                            currentFilledPositions.add(`${row}-${col}`);
                            Logger.log(`Marked (${row},${col}) as filled`);
                        }
                    }
                }
            }

            // Also check filledCoordinates from database
            if (hwTpl?.filledCoordinates) {
                try {
                    const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
                    filledCoordinates.forEach(w => {
                        if (w.positions) {
                            w.positions.forEach(pos => {
                                if (pos.value) {
                                    const posKey = `${pos.row}-${pos.col}`;
                                    currentFilledPositions.add(posKey);
                                    Logger.log(`Added from database: (${pos.row},${pos.col})`);
                                }
                            });
                        }
                    });
                } catch (error) {
                    Logger.error("Error parsing filled coordinates:", error);
                }
            }

            Logger.log("All filled positions:", Array.from(currentFilledPositions).sort());
            Logger.log("Current filled positions:", currentFilledPositions);

            // Count total empty cells
            let totalEmptyCells = 0;
            for (let row = 0; row < 12; row++) {
                for (let col = 0; col < 12; col++) {
                    const positionKey = `${row}-${col}`;
                    if (!currentFilledPositions.has(positionKey)) {
                        totalEmptyCells++;
                    }
                }
            }

            Logger.log(`Total empty cells available: ${totalEmptyCells}`);

            // Debug: Log the current state
            Logger.log("Word to place:", wordToPlace, "Length:", wordLength);
            Logger.log("Current filled positions:", Array.from(currentFilledPositions));
            Logger.log("Square state:", square);

            // Place characters from top-left to bottom-right in empty cells
            let charIndex = 0;

            // First, check if we have enough empty cells
            if (totalEmptyCells < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${totalEmptyCells} empty cells, need ${wordLength}`);
            }

            // Now place the characters
            for (let row = 0; row < 12 && charIndex < wordLength; row++) {
                for (let col = 0; col < 12 && charIndex < wordLength; col++) {
                    const positionKey = `${row}-${col}`;

                    // Check if this position is empty
                    if (!currentFilledPositions.has(positionKey)) {
                        positions.push({
                            row: row,
                            col: col,
                            value: normalizedWord[charIndex]
                        });
                        // Add to currentFilledPositions to prevent reuse
                        currentFilledPositions.add(positionKey);
                        charIndex++;
                        Logger.log(`Placed character ${charIndex}/${wordLength}: ${normalizedWord[charIndex - 1]} at (${row}, ${col})`);
                    }
                }
            }

            Logger.log("Final charIndex:", charIndex, "Word length:", wordLength);
            Logger.log("Positions placed:", positions);

            // Make sure we placed all characters
            if (charIndex < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${charIndex} empty cells, need ${wordLength}`);
            }

            // Create word data with sequential positions
            const wordData = {
                word: normalizedWord,
                text: normalizedWord,
                positions: positions,
                direction: 'scattered',
                length: normalizedWord.length,
                startRow: positions[0]?.row || 0,
                startCol: positions[0]?.col || 0
            };

            // Add the word to the puzzle
            if (onWordAdd) {
                onWordAdd(wordData);
                onShowSnackbar(`Word "${wordToPlace}" placed in puzzle`, "success");
                // Refresh candidate words after word is placed
                if (selectedWord) {
                    findCandidateWordsForWord(selectedWord);
                }
            }

        } catch (error) {
            Logger.error("Error placing word in puzzle:", error);
            onShowSnackbar("Error placing word in puzzle: " + error.message, "error");
        } finally {
            setConfirmDialogOpen(false);
            setWordToPlace(null);
        }
    };

    // Calculate actual filled cells by considering unique coordinates
    const calculateFilledCells = useCallback(() => {
        if (!hwTpl?.filledCoordinates) return 0;
        try {
            const filledCoordinates = JSON.parse(hwTpl.filledCoordinates);
            const uniqueCells = new Set();

            filledCoordinates.forEach(word => {
                if (word.text && word.positions) {
                    // Add each position coordinate to the set
                    word.positions.forEach(pos => {
                        uniqueCells.add(`${pos.row}-${pos.col}`);
                    });
                }
            });

            return uniqueCells.size;
        } catch (error) {
            Logger.error("Error calculating filled cells:", error);
            return 0;
        }
    }, [hwTpl?.filledCoordinates]);

    const emptyCellsCount = 144 - calculateFilledCells();

    // Handle cleanup
    const handleCleanUp = async () => {
        try {
            await AdminCrossBoardService.cleanUp(hwTpl);
            onShowSnackbar("Puzzle cleaned up successfully", "success");
            // Refresh the page after successful cleanup
            window.location.reload();
        } catch (error) {
            Logger.error("Error cleaning up puzzle:", error);
            onShowSnackbar("Error cleaning up puzzle", "error");
        }
    };

    // Handle delete selected word - show confirmation dialog
    const handleDeleteSelectedWord = () => {
        if (!selectedWord || !hwTpl?.id) {
            onShowSnackbar("Please select a word to delete", "warning");
            return;
        }

        // Set the word to delete and show confirmation dialog
        setWordToDelete(selectedWord);
        setDeleteWordDialogOpen(true);
    };

    // Handle confirmation and delete word
    const handleConfirmDeleteWord = async () => {
        if (!wordToDelete || !hwTpl?.id) return;

        try {
            // Get current filled coordinates and remove the selected word
            const filledCoords = hwTpl?.filledCoordinates ? JSON.parse(hwTpl.filledCoordinates) : [];
            const updatedCoords = filledCoords.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete.toUpperCase()
            );

            // Call API to update the puzzle
            await AdminCrossBoardService.updatePuzzleCoordinates(hwTpl.id, updatedCoords, null);

            onShowSnackbar(`Word "${wordToDelete}" removed successfully`, "success");

            // Clear selected word
            setSelectedWord(null);
            setSelectedAssociatedWord(null);
            setAssociatedWords([]);
            setCandidateWords([]);

            // Notify parent to refresh puzzle data
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            Logger.error("Error deleting word:", error);
            onShowSnackbar("Error deleting word", "error");
        } finally {
            setDeleteWordDialogOpen(false);
            setWordToDelete(null);
        }
    };

    // Add words not in use to empty slots (crossword: left-to-right, top-to-bottom; chars can share cells when intersecting)
    const handleAddAllWordsNotInUse = async () => {
        if (!hwTpl?.id) {
            onShowSnackbar("No puzzle available", "warning");
            return;
        }
        if (!wordsNotInUse.length || emptySpaces.length === 0) {
            onShowSnackbar("No words to add or no empty slots", "warning");
            return;
        }

        try {
            const language = hwTpl?.language || "english";
            let workingCoords = hwTpl?.filledCoordinates ? JSON.parse(hwTpl.filledCoordinates) : [];
            let workingSquare = buildSquareFromFilledCoordinates(workingCoords);
            const usedWords = new Set(workingCoords.map((w) => (w.text || "").toUpperCase()).filter(Boolean));
            const wordsToTry = [...wordsNotInUse]
                .filter((w) => w && !usedWords.has(String(w).toUpperCase()))
                .sort((a, b) => b.length - a.length);
            let placedCount = 0;

            for (const word of wordsToTry) {
                const wordStr = String(word).trim().toUpperCase();
                if (!wordStr || usedWords.has(wordStr)) continue;

                const emptySlots = findEmptySpaces(workingSquare);
                for (const slot of emptySlots) {
                    if (slot.pattern.length !== wordStr.length) continue;
                    let fits = true;
                    for (let i = 0; i < slot.pattern.length; i++) {
                        const patternChar = slot.pattern[i];
                        const wordChar = wordStr[i];
                        if (patternChar !== "?") {
                            const nPattern = normalizeLetter(patternChar, language);
                            const nWord = normalizeLetter(wordChar, language);
                            if (nPattern !== nWord) {
                                fits = false;
                                break;
                            }
                        }
                    }
                    if (fits) {
                        const positions = generatePositions(
                            slot.startRow,
                            slot.startCol,
                            slot.direction,
                            wordStr.length,
                            wordStr
                        );
                        const newEntry = {
                            text: wordStr.toLowerCase(),
                            positions
                        };
                        workingCoords = [...workingCoords, newEntry];
                        workingSquare = buildSquareFromFilledCoordinates(workingCoords);
                        usedWords.add(wordStr);
                        placedCount++;
                        break;
                    }
                }
            }

            if (placedCount > 0) {
                await AdminCrossBoardService.updatePuzzleCoordinates(hwTpl.id, workingCoords, null);
                if (onRefresh) onRefresh();
                onShowSnackbar(`${placedCount} word(s) added to puzzle`, "success");
            } else {
                onShowSnackbar("No words could be placed in available slots", "info");
            }
        } catch (err) {
            Logger.error("Error adding words:", err);
            onShowSnackbar("Error adding words: " + (err?.message || err), "error");
        } finally {
            setAddAllWordsDialogOpen(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            {/* Show no empty spaces message if there are no empty spaces */}
            {emptySpaces.length === 0 && square && square.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                    <Alert severity="info" sx={{ maxWidth: 400 }}>
                        There are no empty spaces in this puzzle. All cells are filled with letters.
                    </Alert>
                </Box>
            ) : (
                <>
                    {/* Puzzle Words Section */}
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">
                                Words in Puzzle ({puzzleWords.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={handleDeleteSelectedWord}
                                        disabled={!selectedWord}
                                        sx={{
                                            '&:disabled': {
                                                opacity: 0.3
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" color={selectedWord ? "error" : "action"} />
                                    </IconButton>
                                </span>
                                <IconButton
                                    size="small"
                                    onClick={handleCleanUp}
                                >
                                    <CleaningServicesIcon fontSize="small" color="action" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                            {puzzleWords.length > 0 ? (
                                puzzleWords
                                    .filter(word => word) // Filter out empty/null words
                                    .map((word, index) => (
                                        <Chip
                                            key={index}
                                            label={typeof word === 'string' ? word.toLowerCase() : word}
                                            onClick={() => handleWordSelect(word)}
                                            color={selectedWord === word ? "primary" : "default"}
                                            variant={selectedWord === word ? "filled" : "outlined"}
                                            size="small"
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No words in puzzle yet
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Words Not In Use Section - Words from word network not yet placed in puzzle */}
                    {emptySpaces.length > 0 && (isFrenchPuzzle || wordsNotInUse.length > 0) && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="subtitle2">
                                        Words Not In Use ({isFrenchPuzzle
                                            ? "—"
                                            : filterWordsNotInUseByLength
                                              ? wordsNotInUse.filter(word => word.length === emptyCellsCount).length
                                              : wordsNotInUse.length})
                                    </Typography>
                                    {!isFrenchPuzzle ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => setAddAllWordsDialogOpen(true)}
                                            disabled={emptySpaces.length <= 0 || wordsNotInUse.length === 0}
                                            sx={{ minWidth: 'auto', '&:hover': { backgroundColor: 'action.hover' } }}
                                        >
                                            <AddTaskIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    ) : null}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {!isFrenchPuzzle ? (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        onClick={() => setFilterWordsNotInUseByLength(!filterWordsNotInUseByLength)}
                                        sx={{
                                            fontWeight: 'bold',
                                            letterSpacing: 0.5,
                                            fontSize: '0.75rem',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            backgroundColor: (theme) => filterWordsNotInUseByLength
                                                ? theme.palette.error.main
                                                : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                            color: filterWordsNotInUseByLength ? 'white' : 'error.main',
                                            border: (theme) => `1px solid ${theme.palette.error.main}`,
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: (theme) => theme.palette.error.main,
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Empty: {emptyCellsCount}
                                    </Typography>
                                    ) : null}
                                    {!isFrenchPuzzle && selectedWordForPlacement && wordsNotInUse.some(w => w.toUpperCase() === selectedWordForPlacement.toUpperCase()) && (
                                        <IconButton
                                            size="small"
                                            onClick={handleOpenPlacementDialog}
                                            sx={{
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <AddIcon fontSize="small" color="primary" sx={{ outline: '1px solid', borderRadius: '50%' }} />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                            {isFrenchPuzzle ? (
                                <WordsNotInUseFrenchNotice />
                            ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                                {(() => {
                                    const filteredWords = filterWordsNotInUseByLength
                                        ? wordsNotInUse.filter(word => word.length === emptyCellsCount)
                                        : wordsNotInUse;
                                    const sortedWords = [...filteredWords].sort((a, b) => a.length - b.length);
                                    return sortedWords.length > 0 ? (
                                        sortedWords.map((word, index) => (
                                            <Chip
                                                key={index}
                                                label={
                                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                        <span>{word}</span>
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                bgcolor: 'action.selected',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {word.length}
                                                        </Box>
                                                    </Box>
                                                }
                                                onClick={() => handleWordNotInPuzzleSelect(word)}
                                                color={selectedWordForPlacement?.toUpperCase() === word.toUpperCase() ? "secondary" : "default"}
                                                variant={selectedWordForPlacement?.toUpperCase() === word.toUpperCase() ? "filled" : "outlined"}
                                                size="small"
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {filterWordsNotInUseByLength
                                                ? `No words found with length = ${emptyCellsCount} characters`
                                                : "No words not in use"}
                                        </Typography>
                                    );
                                })()}
                            </Box>
                            )}
                        </Box>
                    )}

                    {/* Candidates for scattered placement - Show candidates based on synonyms/associations */}
                    {selectedWord && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Candidates for scattered placement
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        onClick={() => setFilterByLength(!filterByLength)}
                                        sx={{
                                            fontWeight: 'bold',
                                            letterSpacing: 0.5,
                                            fontSize: '0.75rem',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            backgroundColor: (theme) => filterByLength 
                                                ? theme.palette.error.main 
                                                : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                            color: filterByLength ? 'white' : 'error.main',
                                            border: (theme) => `1px solid ${theme.palette.error.main}`,
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: (theme) => theme.palette.error.main,
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Empty: {emptyCellsCount}
                                    </Typography>
                                    {selectedWordForPlacement && (
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleOpenPlacementDialog}
                                                disabled={true}
                                                sx={{
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                }}
                                            >
                                                <AddIcon fontSize="small" color="primary" sx={{ outline: '1px solid', borderRadius: '50%' }} />
                                            </IconButton>
                                        </span>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflow: 'auto' }}>
                                {loadingCandidates ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Searching for candidates...
                                    </Typography>
                                ) : (() => {
                                    const filteredCandidates = filterByLength 
                                        ? candidateWords.filter(candidate => candidate.word.length === emptyCellsCount)
                                        : candidateWords;
                                    return filteredCandidates.length > 0 ? (
                                        filteredCandidates.map((candidate, index) => (
                                            <Chip
                                                key={index}
                                                label={`${candidate.word.toLowerCase()} (${candidate.word.length})`}
                                                size="small"
                                                variant={selectedWordForPlacement === candidate.word ? "filled" : "outlined"}
                                                color={selectedWordForPlacement === candidate.word ? "primary" : "warning"}
                                                onClick={() => handleWordNotInPuzzleSelect(candidate.word)}
                                                sx={{ cursor: 'pointer' }}
                                                title={`${candidate.type} of "${candidate.source}" - Click to select for placement`}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {filterByLength 
                                                ? `No candidate words found with length = ${emptyCellsCount} characters`
                                                : `No candidate words found with length ≤ ${emptyCellsCount} characters`}
                                        </Typography>
                                    );
                                })()}
                            </Box>
                        </Box>
                    )}

                    {/* Associated Words Section */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">
                                Associated Words {loading && "(Searching...)"}
                            </Typography>
                            {associatedWords.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>
                                        <IconButton
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            size="small"
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                    </span>
                                    <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                                        {currentPage} / {totalPages}
                                    </Typography>
                                    <span>
                                        <IconButton
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                            size="small"
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </span>
                                </Box>
                            )}
                        </Box>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <Typography>Searching for associated words...</Typography>
                            </Box>
                        ) : associatedWords.length > 0 ? (
                            <List sx={{ flex: 1, overflow: 'auto' }}>
                                {paginatedWords.map((wordData, index) => (
                                    <CrossCandEntry
                                        key={index}
                                        candidate={wordData}
                                        isSelected={selectedAssociatedWord?.word === wordData.word}
                                        onWordSelect={handleAssociatedWordSelect}
                                        onWordAdd={handleAddAssociatedWord}
                                    />
                                ))}
                            </List>
                        ) : selectedWord ? (
                            <Alert severity="info">
                                No associated words found that can fit in the puzzle
                            </Alert>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Select a word from the puzzle to find associated words
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {/* Confirmation Dialog for Word Placement */}
            <ConfirmDialog
                open={confirmDialogOpen}
                setOpen={setConfirmDialogOpen}
                title="Place Word in Puzzle"
                message={[`Would you like to put "${wordToPlace}" into all empty cells? Characters will be placed from top-left to bottom-right.`, `This puzzle will be a Hidden message puzzle.`]}
                targetName={wordToPlace || ""}
                targetId=""
                callback={handleConfirmWordPlacement}
            />

            {/* Confirmation Dialog for Word Deletion */}
            <ConfirmDialog
                open={deleteWordDialogOpen}
                setOpen={(open) => {
                    setDeleteWordDialogOpen(open);
                    if (!open) {
                        setWordToDelete(null);
                    }
                }}
                title="Delete Word"
                message={`Would you like to delete the word "${wordToDelete}"?`}
                targetName=""
                targetId=""
                callback={handleConfirmDeleteWord}
            />

            {/* Confirmation Dialog for Add All Words Not In Use */}
            <ConfirmDialog
                open={addAllWordsDialogOpen}
                setOpen={setAddAllWordsDialogOpen}
                title="Add Words to Puzzle"
                message="Would you like to add words from 'Words Not In Use' to empty slots? Words will be placed left-to-right or top-to-bottom. Intersecting words share cells."
                targetName=""
                targetId=""
                callback={handleAddAllWordsNotInUse}
            />
        </Box>
    );
}

